import { useParams } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { getErrorMessage, retryUntil, textToTitleCase } from '../../../utils/helpers'
import { Bounce, toast } from 'react-toastify'
import InfiniteScroll from 'react-infinite-scroll-component'
import Skeleton from 'react-loading-skeleton'
import { Gig, Milestone, MilestoneStep } from 'types/collaboration'
import { AppDispatch, RootState } from 'store'
import { Badge } from 'reactstrap'
import { processCollaborationMilestoneStep } from 'services/api/collaboration'
import { isDesktopMediaQuery } from 'Components/constants/layout'
import { fetchSocialPostsThunk } from 'slices/influencer/thunk'
import { fetchLatestInfluencerSocialMedia } from 'services/api/influencer'
import inModal from 'utils/hoc/inModal'
import { MediaProductType } from 'types/influencer'
import SocialPostCard from 'Components/influencer/SocialPostCard'

interface SocialPostSelectionProps {
    step: MilestoneStep,
    gigs: Gig[]
    activeMilestone: Milestone
    isActive: boolean,
    onClose: (refetch?: boolean) => void,
    setSelectedMedia: (post: any) => void
}

const SocialPostSelection: React.FC<SocialPostSelectionProps> = ({ activeMilestone, onClose, step }) => {

    const { campaignId, collaborationId, milestoneId } = useParams();

    const dispatch: AppDispatch = useDispatch();

    const hubId = useSelector((state: RootState) => state.auth.selectedHubId);

    const [submitting, setSubmitting] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
    const [fetchingLatestMedia, setFetchingLatestMedia] = useState(false);

    const collaboration = useSelector((state: RootState) => state.legacyCollaborations.items[campaignId!]?.[collaborationId!]?.collaboration);
    const socialAccountId = collaboration?.social_account?.uuid ?? null;
    const influencerId = collaboration?.influencer?.uuid;
    

    const mediaProductType = useMemo(() => {
        switch (step.gig_step.attributes.media_product_type) {
            case 'REELS':
                return MediaProductType.REEL
            case 'STORY':
                return MediaProductType.STORY
            case 'VIDEO':
                return MediaProductType.VIDEO
            default:
                return MediaProductType.FEED
        }
    }, [step.gig_step.attributes.media_product_type])

    const [selectedPosts, setSelectedPosts] = useState<string[]>([]);
    const [alreadySelectedPosts, setAlreadySelectedPosts] = useState<string[]>([]);


    const [approvedPosts, setApprovedPosts] = useState<string[]>([]);
    const [rejectedPosts, setRejectedPosts] = useState<string[]>([]);

    const data = useSelector(
        (state: RootState) => state.influencers?.social_media?.[collaboration?.social_account?.uuid!]?.[mediaProductType] ?? null
    );

    const posts = data ? data.items : [];

    useEffect(() => {
        if (data) {
            setLastFetchedAt(data.last_fetched_at_server);
        }
    }, [data?.last_fetched_at_server])


    const has_more = data ? data.has_more : true;
    const page = data ? data.page : 1;

    const loading = useSelector((state: RootState) => state.influencers.mediaLoading);

    const isDesktop = useMediaQuery(isDesktopMediaQuery)


    const fetchCollaborationMedia = useCallback(async (pageNumber: number, force = false) => {
        if (!campaignId || !socialAccountId) return null;
        try {
            const latestMedia = await dispatch(fetchSocialPostsThunk({ socialAccountId, mediaProductType, page: pageNumber, force })).unwrap();
            return latestMedia.media;
        } catch (error) {
            console.log('err,', error)
            return null
        }
    }, [campaignId, dispatch, mediaProductType, socialAccountId])



    useEffect(() => {
        if (!campaignId) return;
        fetchCollaborationMedia(1);
    }, [mediaProductType, campaignId, fetchCollaborationMedia, socialAccountId]);

    const nextPage = () => {
        if (has_more && !loading) {
            const nextPageNumber = page + 1;
            fetchCollaborationMedia(nextPageNumber)
        }
    }

    useEffect(() => {
        if (collaboration) {
            let selected_posts: string[] = [], rejected_posts: string[] = [], approved_posts: string[] = [];
            if (Array.isArray(collaboration?.posts)) {
                for (let post of collaboration.posts) {
                    if (post.status === 'approved') approved_posts.push(post.social_post.uuid);
                    if (post.status === 'rejected') rejected_posts.push(post.social_post.uuid);
                    if (post.status === 'pending') selected_posts.push(post.social_post.uuid);
                }
            }
            setSelectedPosts(selected_posts);
            setAlreadySelectedPosts(selected_posts);
            setApprovedPosts(approved_posts);
            setRejectedPosts(rejected_posts);
        }
    }, [collaboration])


    const fetchLatestMedia = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {

        if (fetchingLatestMedia || !socialAccountId || !hubId) return;
        try {
            setFetchingLatestMedia(true);
            const loadingToastId = toast("Fetching latest media", {
                position: "top-center",
                autoClose: 32000,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                hideProgressBar: false,
                theme: "colored",
                transition: Bounce,
                type: 'info',
                isLoading: true,
                closeButton: false
            })
            await fetchLatestInfluencerSocialMedia(hubId, socialAccountId, influencerId);

            await retryUntil(async () => {
                const prev = lastFetchedAt;
                const latestData = await fetchCollaborationMedia(1, true);
                const latest = (latestData?.last_fetched_at ?? null);
                return latest !== prev;
            });

            toast.update(loadingToastId, {
                type: 'success',
                isLoading: false,
                render: "Showing latest media",
                autoClose: 2000,
            });
        } catch (error: any) {
            console.log(error);
            toast.error(getErrorMessage(error));
        } finally {
            setFetchingLatestMedia(false)
        }
    }

    const selectPost = (postId: string) => {
        if (approvedPosts.includes(postId) || rejectedPosts.includes(postId) || alreadySelectedPosts.includes(postId)) return;

        if (selectedPosts.includes(postId)) {
            setSelectedPosts([...selectedPosts.filter((pid) => pid !== postId)]);
        } else {
            setSelectedPosts([...selectedPosts, postId])
        }
    }


    const onSubmit = async () => {
        if (!campaignId || !selectedPosts.length || !milestoneId || !collaborationId || !hubId) return;
        try {
            setSubmitting(true);
            await processCollaborationMilestoneStep(hubId, campaignId, collaborationId, milestoneId, { step: step.gig_step.slug, social_posts: selectedPosts.filter((selectedPost) => !alreadySelectedPosts.includes(selectedPost)) });
            onClose(true);
        } catch (error: any) {
            console.log(error);
            toast.error(error?.response?.data?.message ?? "Unknown error occurred.")
        } finally {
            setSubmitting(false);
        }
    }


    const getMediaProductType = (multiple: boolean = false) => {
        switch (mediaProductType) {
            case MediaProductType.REEL:
                return multiple ? 'reels' : 'reel';
            case MediaProductType.STORY:
                return multiple ? 'stories' : 'story';
            case MediaProductType.VIDEO:
                return multiple ? 'videos' : 'video';
            default:
                return multiple ? 'posts' : 'post';
        }
    }

    return (
        <React.Fragment>
            <div className="">
                <div className="post-selection-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <span>All {textToTitleCase(getMediaProductType(true))}</span>
                            <p className='m-0'>{activeMilestone.content_media_types[mediaProductType]} {getMediaProductType(Number(activeMilestone.content_media_types[mediaProductType]) > 1)} to be uploaded</p>
                        </div>

                        <button type="button" className="btn btn-xxs ms-2 btn-primary rounded-sm" title="Fetch Latest Media" onClick={fetchLatestMedia}>
                            <i className="mdi mdi-sync"></i>
                        </button>
                    </div>
                </div>
                <div style={{ height: '56vh', overflowY: 'auto' }} id="scrollableContent">
                    <InfiniteScroll
                        className="row gx-0"
                        hasMore={has_more}
                        next={nextPage}
                        dataLength={posts.length}
                        loader={<></>}
                        scrollableTarget='scrollableContent'
                    >
                        {posts.map((post, postIndex) => (
                            <div className="col-6 p-1 position-relative" key={postIndex} style={{ position: 'relative' }} >
                                <SocialPostCard newStyle={true} post={post} onPostClick={selectPost} />
                                {
                                    !approvedPosts.includes(post.uuid) && !rejectedPosts.includes(post.uuid) &&
                                    <div onClick={() => selectPost(post.uuid)} className='post-check-wrapper' style={{ ...(post.type === 'STORY' ? { borderRadius: '0px', height: '50%' } : {}) }}>
                                        <input type="checkbox" className="form-check-input text-white bg-transparent post-select-input" style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            border: "2px solid white"
                                        }}
                                            disabled={approvedPosts.includes(post.uuid) || rejectedPosts.includes(post.uuid) || alreadySelectedPosts.includes(post.uuid)}
                                            checked={selectedPosts.includes(post.uuid)}
                                            onChange={() => selectPost(post.uuid)}
                                        />
                                    </div>
                                }
                                {
                                    (approvedPosts.includes(post.uuid) || rejectedPosts.includes(post.uuid) || alreadySelectedPosts.includes(post.uuid)) &&
                                    <div style={{ position: "absolute", top: "20px", marginLeft: 'auto', marginRight: 'auto', left: 0, right: 0, textAlign: 'center' }}>
                                        {
                                            approvedPosts.includes(post.uuid) &&
                                            <Badge color='success'><i className="fa fa-check me-1"></i>Approved</Badge>
                                        }
                                        {
                                            rejectedPosts.includes(post.uuid) &&
                                            <Badge color="danger"><i className="fa fa-times me-1"></i> Declined</Badge>
                                        }
                                        {
                                            alreadySelectedPosts.includes(post.uuid) &&
                                            <Badge color="warning"><i className="fa fa-clock me-1"></i> Pending</Badge>
                                        }
                                    </div>
                                }
                            </div>
                        ))}
                    </InfiniteScroll>
                    {
                        loading &&
                        <div className="row m-0">
                            {
                                Array.from({ length: 4 }).map((_, i) => {
                                    return (
                                        <div className="col-6 p-1" key={i}>
                                            <Skeleton style={{ width: '100%', height: isDesktop ? '270px' : '145px', padding: 0, margin: 0, borderRadius: '10px' }} />
                                        </div>
                                    )
                                })
                            }
                        </div>
                    }
                    {
                        !loading && !posts.length &&
                        <span className="w-100 d-flex justify-content-center align-items-center" style={{ height: 'calc(100% - 60px)' }}>No media found since collaboration started!</span>
                    }
                </div>
            </div>
            <div className="milestone-step-actions">
                <button className="theme-btn theme-btn-primary" disabled={selectedPosts.length === 0 || submitting} onClick={onSubmit}>Send for Approval</button>
            </div>
        </React.Fragment>

    )
}

const SocialPostSelectionModal: React.FC<SocialPostSelectionProps> = (props) => {
    const { step } = props
    let contentType = "Post";
    switch (step.gig_step.attributes.media_product_type) {
        case 'REELS':
            contentType = 'Reel';
            break;
        case 'STORY':
            contentType = 'Story';
            break;
        default:
            contentType = "Post"
    }
    const title = `Upload Live ${contentType}`

    const ModalComponent = useMemo(() => inModal(SocialPostSelection, title), [title])
    return <ModalComponent {...props} />
}


export default SocialPostSelectionModal;